import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';
import { today } from '../../lib/dates.js';

// Reads today's daily_briefing for the MITs, today's daily_checkout for the
// done-states. Checking an MIT writes to daily_checkout AND cascades to the
// linked task (status=done, completed_at=now) if mit_X_task_id is set.
export default function TodayMITs() {
  const [briefing, setBriefing] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const date = today();

    async function load() {
      const [{ data: b }, { data: c }] = await Promise.all([
        supabase.from('daily_briefing').select('*').eq('date', date).maybeSingle(),
        supabase.from('daily_checkout').select('*').eq('date', date).maybeSingle(),
      ]);
      if (!mounted) return;
      setBriefing(b ?? null);
      setCheckout(c ?? null);
      setLoading(false);
    }
    load();

    // Realtime: sync from other tabs
    const channels = ['daily_briefing', 'daily_checkout'].map((tbl) =>
      supabase
        .channel(`${tbl}:${date}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: tbl, filter: `date=eq.${date}` },
          (payload) => {
            if (tbl === 'daily_briefing' && payload.new) setBriefing(payload.new);
            if (tbl === 'daily_checkout' && payload.new) setCheckout(payload.new);
          }
        )
        .subscribe()
    );

    return () => {
      mounted = false;
      channels.forEach((c) => supabase.removeChannel(c));
    };
  }, []);

  async function toggleMIT(slot, currentDone) {
    const date = today();
    const nextDone = !currentDone;

    // Optimistic local update
    setCheckout((prev) => ({
      ...(prev ?? { date }),
      [`mit_${slot}_done`]: nextDone,
    }));

    // Upsert daily_checkout
    const patch = { date, [`mit_${slot}_done`]: nextDone };
    await supabase
      .from('daily_checkout')
      .upsert({ ...(checkout ?? {}), ...patch }, { onConflict: 'date' });

    // Cascade to linked task if present
    const taskId = briefing?.[`mit_${slot}_task_id`];
    if (taskId) {
      await supabase
        .from('tasks')
        .update({
          status: nextDone ? 'done' : 'pending',
          completed_at: nextDone ? new Date().toISOString() : null,
        })
        .eq('id', taskId);
    }
  }

  if (loading) return <Card><Placeholder>Loading…</Placeholder></Card>;

  if (!briefing) {
    return (
      <Card>
        <Placeholder>
          No briefing yet today.
          <div className="text-xs text-text-muted mt-1">
            Head to Morning Briefing to set your MITs.
          </div>
        </Placeholder>
      </Card>
    );
  }

  const mits = [1, 2, 3].map((i) => ({
    slot: i,
    text: briefing[`mit_${i}_text`] || '(empty)',
    done: !!checkout?.[`mit_${i}_done`],
    taskId: briefing[`mit_${i}_task_id`],
  }));

  const doneCount = mits.filter((m) => m.done).length;

  return (
    <Card badge={`${doneCount}/3`}>
      <ul className="space-y-2">
        {mits.map((m) => (
          <li key={m.slot} className="flex items-start gap-3">
            <button
              onClick={() => toggleMIT(m.slot, m.done)}
              className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition shrink-0 ${
                m.done
                  ? 'bg-accent-green border-accent-green text-white'
                  : 'border-border-base hover:border-accent-blue'
              }`}
              aria-label={`Toggle MIT ${m.slot}`}
            >
              {m.done && (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8l3.5 3.5L13 5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            <span
              className={`flex-1 text-sm transition ${
                m.done ? 'line-through text-text-muted' : 'text-text-primary'
              }`}
            >
              {m.text}
              {m.taskId && (
                <span className="ml-2 text-[10px] uppercase tracking-wide text-text-muted">
                  · linked task
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Card({ children, badge }) {
  return (
    <div className="bg-bg-card border border-border-base rounded p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-text-secondary">
          🎯 Today's MITs
        </h3>
        {badge && (
          <span className="text-[10px] px-2 py-0.5 bg-accent-green/15 text-accent-green rounded">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function Placeholder({ children }) {
  return <div className="text-sm py-4 text-center text-text-muted">{children}</div>;
}
